import { timestampDate } from "@bufbuild/protobuf/wkt";
import { AlertTriangleIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNotifications } from "@/hooks/useUserQueries";
import { cn } from "@/lib/utils";
import { UserNotification, UserNotification_Type } from "@/types/proto/api/v1/user_service_pb";

interface ToastData {
  id: string;
  tickerSymbol: string;
  contractId: string;
  triggerType: string;
  operator: string;
  targetValue: number;
  currentValue: number;
  contractType: string;
  idea: string;
  timestamp: Date;
}

const TriggerNotification = () => {
  const { data: notifications = [] } = useNotifications();
  const [activeToasts, setActiveToasts] = useState<ToastData[]>([]);
  const [dismissedNotifications, setDismissedNotifications] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Check for new TRIGGER_SCHEDULER notifications
    notifications.forEach((notification: UserNotification) => {
      if (notification.type === UserNotification_Type.TRIGGER_SCHEDULER && !dismissedNotifications.has(notification.name)) {
        try {
          const content = notification.title || "{}";
          const data = JSON.parse(content);
          const toastData: ToastData = {
            id: notification.name,
            tickerSymbol: data.ticker || "Unknown",
            contractId: data.contract_id || "",
            triggerType: data.type || "price",
            operator: data.operator || "=",
            targetValue: data.target_value || 0,
            currentValue: data.current_price || 0,
            contractType: data.contract_type || "",
            idea: data.idea || "",
            timestamp: notification.createTime ? timestampDate(notification.createTime)! : new Date(),
          };

          // Add toast if not already active and not too old (within last 30 seconds)
          const isRecent = Date.now() - toastData.timestamp.getTime() < 30000;
          const isActive = activeToasts.some((t) => t.id === toastData.id);

          if (!isActive && isRecent) {
            setActiveToasts((prev) => [...prev, toastData]);

            // Auto-dismiss after 10 seconds
            setTimeout(() => {
              setActiveToasts((prev) => prev.filter((t) => t.id !== toastData.id));
              setDismissedNotifications((prev) => new Set([...prev, toastData.id]));
            }, 10000);
          }
        } catch (error) {
          console.error("Failed to parse notification:", error);
        }
      }
    });
  }, [notifications, activeToasts, dismissedNotifications]);

  const handleDismiss = (id: string) => {
    setActiveToasts((prev) => prev.filter((t) => t.id !== id));
    setDismissedNotifications((prev) => new Set([...prev, id]));
  };

  if (activeToasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-3">
      {activeToasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            "w-96 rounded-lg shadow-lg border bg-card p-4",
            "animate-in slide-in-from-right-full fade-in",
            "duration-300 ease-in-out",
          )}
        >
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <AlertTriangleIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium mb-1">触发条件达成: {toast.tickerSymbol}</p>
                  <p className="text-xs text-muted-foreground mb-2">
                    价格 {toast.operator} {toast.targetValue} · 当前 ${toast.currentValue?.toFixed(2) || "N/A"}
                  </p>

                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                      {toast.contractType}
                    </span>

                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                      {toast.triggerType}
                    </span>
                  </div>

                  {toast.idea && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{toast.idea}</p>}
                </div>

                {/* Close Button */}
                <button
                  onClick={() => handleDismiss(toast.id)}
                  className="flex-shrink-0 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TriggerNotification;
